import {FC, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, IconButton, Typography} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {FormattedMessage, useIntl} from 'react-intl';
import {DayOfWeek, EnergyType, Month, PriceItemDTO} from 'src/types/pricing';
import {formatPrice} from 'src/utils/helper';
import {AMPInput} from 'src/components/common/AMPInput';
import AMPModal from 'src/components/common/AMPModal';
import {
    cornerCellSx,
    dayNameSx,
    hourHeaderSx,
    matrixFrameSx,
    matrixTrackSx,
    scrollButtonSx,
    scrollControlsSx,
    scrollHintSx,
    timeCellSx,
    weekHeaderSx,
    weekMatrixSx,
    weekRowSx,
} from './WeeklyMatrix.sx';

interface WeeklyMatrixProps {
    priceItems: PriceItemDTO[];
    isLoading?: boolean;
    onSetPrice?: (cells: Array<{ day: DayOfWeek; hour: number }>, price: number) => void;
    onClearPrice?: (cells: Array<{ day: DayOfWeek; hour: number }>) => void;
    onCopyFromMonth?: (sourceMonth: Month) => void;
    currentMonth?: Month;
    energyType?: EnergyType;
    currency?: string;
    readOnly?: boolean;
}

const DAYS: DayOfWeek[] = [
    DayOfWeek.MONDAY,
    DayOfWeek.TUESDAY,
    DayOfWeek.WEDNESDAY,
    DayOfWeek.THURSDAY,
    DayOfWeek.FRIDAY,
    DayOfWeek.SATURDAY,
    DayOfWeek.SUNDAY,
];

// DAY_LABELS map DayOfWeek enum to translation keys
const DAY_TRANSLATION_KEYS: Record<DayOfWeek, string> = {
    [DayOfWeek.MONDAY]: 'MONDAY',
    [DayOfWeek.TUESDAY]: 'TUESDAY',
    [DayOfWeek.WEDNESDAY]: 'WEDNESDAY',
    [DayOfWeek.THURSDAY]: 'THURSDAY',
    [DayOfWeek.FRIDAY]: 'FRIDAY',
    [DayOfWeek.SATURDAY]: 'SATURDAY',
    [DayOfWeek.SUNDAY]: 'SUNDAY',
};

const HOURS = Array.from({length: 24}, (_, i) => i);

// The grid's own track sizes, kept here because the scroll controls do arithmetic with them.
// They must match `gridTemplateColumns` in the stylesheet.
const DAY_COLUMN_WIDTH = 100;
const HOUR_COLUMN_WIDTH = 40;

const ENERGY_TYPE_COLORS: Record<EnergyType, string> = {
    [EnergyType.REGULAR_NETWORK]: '#DEF0FC', // Light blue
    [EnergyType.SOLAR]: '#FFE8D6', // Light orange from summer season color
    [EnergyType.BATTERY_STORAGE]: '#E3F5E6', // Light green from spring season color
};

const MONTHS: Month[] = [
    Month.JANUARY, Month.FEBRUARY, Month.MARCH, Month.APRIL,
    Month.MAY, Month.JUNE, Month.JULY, Month.AUGUST,
    Month.SEPTEMBER, Month.OCTOBER, Month.NOVEMBER, Month.DECEMBER,
];

// Season colors matching the main pricing page
const SEASON_COLORS: Record<string, { main: string; light: string }> = {
    winter: {main: '#007fdc', light: '#DEF0FC'},
    spring: {main: '#399646', light: '#E3F5E6'},
    summer: {main: '#ff7d22', light: '#FFE8D6'},
    autumn: {main: '#D92156', light: '#FFE3E9'},
};

const getMonthSeasonColor = (month: Month): { main: string; light: string } => {
    switch (month) {
        case Month.DECEMBER:
        case Month.JANUARY:
        case Month.FEBRUARY:
            return SEASON_COLORS.winter;
        case Month.MARCH:
        case Month.APRIL:
        case Month.MAY:
            return SEASON_COLORS.spring;
        case Month.JUNE:
        case Month.JULY:
        case Month.AUGUST:
            return SEASON_COLORS.summer;
        case Month.SEPTEMBER:
        case Month.OCTOBER:
        case Month.NOVEMBER:
            return SEASON_COLORS.autumn;
        default:
            return SEASON_COLORS.winter;
    }
};

export const WeeklyMatrix: FC<WeeklyMatrixProps> = ({
                                                        priceItems,
                                                        onSetPrice,
                                                        onClearPrice,
                                                        onCopyFromMonth,
                                                        currentMonth,
                                                        energyType,
                                                        currency,
                                                        readOnly = false
                                                    }) => {
    const intl = useIntl();
    /**
     * The week is 1080px of grid in about 370px of screen, so it always has somewhere to go.
     *
     * It was scrollable all along - `overflow-x: auto`, content wider than the port - and still felt
     * frozen, because there was no way to ask it to move: macOS hides the scrollbar until something
     * scrolls, a mouse wheel only scrolls vertically, and dragging inside the grid is how cells are
     * selected. So the controls are explicit: two buttons, and the wheel turned sideways while the
     * pointer is over the matrix.
     */
    const scrollPortRef = useRef<HTMLDivElement | null>(null);
    const [scrollState, setScrollState] = useState({canLeft: false, canRight: false, hours: ''});

    const readScroll = useCallback(() => {
        const port = scrollPortRef.current;
        if (!port) {
            return;
        }
        // Which hours are actually on screen, so the buttons say where you are rather than only
        // offering to move. The day column is 100px wide and pinned, the hours are 40px each.
        const first = Math.min(23, Math.max(0, Math.round(port.scrollLeft / HOUR_COLUMN_WIDTH)));
        const fit = Math.floor((port.clientWidth - DAY_COLUMN_WIDTH) / HOUR_COLUMN_WIDTH);
        const last = Math.min(23, Math.max(first, first + fit - 1));
        // A pixel of slack: sub-pixel widths otherwise leave the right-hand button live at the end.
        setScrollState({
            canLeft: port.scrollLeft > 1,
            canRight: port.scrollLeft + port.clientWidth < port.scrollWidth - 1,
            hours: `${String(first).padStart(2, '0')} – ${String(last).padStart(2, '0')}`,
        });
    }, []);

    useEffect(() => {
        const port = scrollPortRef.current;
        if (!port) {
            return;
        }
        readScroll();
        port.addEventListener('scroll', readScroll, {passive: true});
        const observer = new ResizeObserver(readScroll);
        observer.observe(port);

        // A vertical wheel over the matrix moves it sideways - the gesture people already make when
        // something is too wide, and the one a mouse cannot otherwise perform.
        const onWheel = (event: WheelEvent) => {
            if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) {
                return;
            }
            const before = port.scrollLeft;
            port.scrollLeft += event.deltaY;
            if (port.scrollLeft !== before) {
                event.preventDefault();
            }
        };
        port.addEventListener('wheel', onWheel, {passive: false});

        return () => {
            port.removeEventListener('scroll', readScroll);
            port.removeEventListener('wheel', onWheel);
            observer.disconnect();
        };
    }, [readScroll]);

    const scrollByHours = useCallback((direction: 1 | -1) => {
        // Four hours a press: enough to feel like progress, little enough to keep your place.
        scrollPortRef.current?.scrollBy({left: direction * 4 * HOUR_COLUMN_WIDTH, behavior: 'smooth'});
    }, []);

    const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
    const [isDragging, setIsDragging] = useState(false);
    const [showPriceModal, setShowPriceModal] = useState(false);
    const [showCopyModal, setShowCopyModal] = useState(false);
    const [selectedSourceMonth, setSelectedSourceMonth] = useState<Month | null>(null);
    const [newPrice, setNewPrice] = useState<string>('0');
    const dragStartCell = useRef<string | null>(null);

    const selectionColor = energyType ? ENERGY_TYPE_COLORS[energyType] : ENERGY_TYPE_COLORS[EnergyType.REGULAR_NETWORK];

    // Border colors for each energy type
    const getBorderColor = () => {
        if (energyType === EnergyType.SOLAR) return '#ff7d22';
        if (energyType === EnergyType.BATTERY_STORAGE) return '#399646';
        return '#007fdc';
    };

    const borderColor = getBorderColor();

    // Clear selection when an energy type changes
    useEffect(() => {
        setSelectedCells(new Set());
    }, [energyType]);

    // Create a map for a quick price lookup
    const priceMap = useMemo(() => {
        const map = new Map<string, number>();
        priceItems.forEach((item) => {
            const key = `${item.dayOfWeek}-${item.hourOfDay}`;
            map.set(key, item.itemPrice);
        });
        return map;
    }, [priceItems]);

    const getPrice = (day: DayOfWeek, hour: number): number | undefined => {
        const key = `${day}-${hour}`;
        return priceMap.get(key);
    };

    const getCellKey = (day: DayOfWeek, hour: number): string => `${day}-${hour}`;

    const handleMouseDown = useCallback((day: DayOfWeek, hour: number, event: React.MouseEvent) => {
        if (readOnly) return;

        // Only a real mouse press starts a selection. A touch arrives here as a synthesised mouse
        // event with no button pressed, and treating it as a drag is what stopped the week from
        // scrolling under a finger: the gesture was spent selecting cells instead.
        if (event.buttons !== undefined && event.buttons !== 1) {
            return;
        }

        event.preventDefault();
        const cellKey = getCellKey(day, hour);
        dragStartCell.current = cellKey;
        setIsDragging(true);

        // Toggle cell selection
        setSelectedCells((prev) => {
            const newSet = new Set(prev);
            if (newSet.has(cellKey)) {
                newSet.delete(cellKey);
            } else {
                newSet.add(cellKey);
            }
            return newSet;
        });
    }, [readOnly]);

    const handleMouseEnter = useCallback((day: DayOfWeek, hour: number) => {
        if (isDragging) {
            const cellKey = getCellKey(day, hour);
            setSelectedCells((prev) => {
                const newSet = new Set(prev);
                newSet.add(cellKey);
                return newSet;
            });
        }
    }, [isDragging]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        dragStartCell.current = null;
    }, []);

    const handleDayClick = useCallback((day: DayOfWeek) => {
        const dayCells = HOURS.map((hour) => getCellKey(day, hour));
        setSelectedCells((prev) => {
            const newSet = new Set(prev);
            // Check if all day cells are already selected
            const allSelected = dayCells.every((cell) => prev.has(cell));

            if (allSelected) {
                // Remove all day cells
                dayCells.forEach((cell) => newSet.delete(cell));
            } else {
                // Add all day cells
                dayCells.forEach((cell) => newSet.add(cell));
            }
            return newSet;
        });
    }, []);

    const handleHourClick = useCallback((hour: number) => {
        const hourCells = DAYS.map((day) => getCellKey(day, hour));
        setSelectedCells((prev) => {
            const newSet = new Set(prev);
            // Check if all hour cells are already selected
            const allSelected = hourCells.every((cell) => prev.has(cell));

            if (allSelected) {
                // Remove all hour cells
                hourCells.forEach((cell) => newSet.delete(cell));
            } else {
                // Add all hour cells
                hourCells.forEach((cell) => newSet.add(cell));
            }
            return newSet;
        });
    }, []);

    const handleClearSelection = useCallback(() => {
        setSelectedCells(new Set());
    }, []);

    const handleOpenPriceModal = useCallback(() => {
        if (selectedCells.size > 0) {
            setNewPrice('0');
            setShowPriceModal(true);
        }
    }, [selectedCells]);

    const handleClosePriceModal = useCallback(() => {
        setShowPriceModal(false);
    }, []);

    const handleSetPrice = useCallback(() => {
        if (onSetPrice && selectedCells.size > 0) {
            // Validate price
            const priceNumber = parseFloat(newPrice);
            if (isNaN(priceNumber) || newPrice === '' || priceNumber < 0) {
                return; // Don't proceed if invalid
            }

            // Convert selected cells to array of {day, hour}
            const cells = Array.from(selectedCells).map((cellKey) => {
                const [day, hour] = cellKey.split('-');
                return {day: day as DayOfWeek, hour: parseInt(hour, 10)};
            });

            onSetPrice(cells, priceNumber);
            setShowPriceModal(false);
            setSelectedCells(new Set());
        }
    }, [onSetPrice, selectedCells, newPrice]);

    const handleClearPrice = useCallback(() => {
        if (onClearPrice && selectedCells.size > 0) {
            // Convert selected cells to array of {day, hour}
            const cells = Array.from(selectedCells).map((cellKey) => {
                const [day, hour] = cellKey.split('-');
                return {day: day as DayOfWeek, hour: parseInt(hour, 10)};
            });

            onClearPrice(cells);
            setSelectedCells(new Set());
        }
    }, [onClearPrice, selectedCells]);

    const handleOpenCopyModal = useCallback(() => {
        setSelectedSourceMonth(null);
        setShowCopyModal(true);
    }, []);

    const handleCloseCopyModal = useCallback(() => {
        setShowCopyModal(false);
        setSelectedSourceMonth(null);
    }, []);

    const handleSelectSourceMonth = useCallback((month: Month) => {
        setSelectedSourceMonth(month);
    }, []);

    const handleConfirmCopy = useCallback(() => {
        if (onCopyFromMonth && selectedSourceMonth) {
            onCopyFromMonth(selectedSourceMonth);
            setShowCopyModal(false);
            setSelectedSourceMonth(null);
        }
    }, [onCopyFromMonth, selectedSourceMonth]);

    return (
        <>
            <Box onMouseUp={handleMouseUp} sx={matrixFrameSx}>
                {/* Shown only while there is somewhere to go, so a wide screen never sees them. */}
                {(scrollState.canLeft || scrollState.canRight) && (
                    <Box sx={scrollControlsSx}>
                        <Typography sx={scrollHintSx}>
                            {scrollState.hours}
                        </Typography>
                        <IconButton
                            sx={scrollButtonSx}
                            disabled={!scrollState.canLeft}
                            onClick={() => scrollByHours(-1)}
                            aria-label="previous hours"
                        >
                            <ArrowBackIosNewIcon fontSize="inherit"/>
                        </IconButton>
                        <IconButton
                            sx={scrollButtonSx}
                            disabled={!scrollState.canRight}
                            onClick={() => scrollByHours(1)}
                            aria-label="next hours"
                        >
                            <ArrowForwardIosIcon fontSize="inherit"/>
                        </IconButton>
                    </Box>
                )}
                <Box
                    ref={scrollPortRef}
                    sx={weekMatrixSx}
                >
                    {/* One track, sized once to the week's real width. The scrollport scrolls this
                        single child rather than inferring its extent from eight separate rows. */}
                    <Box sx={matrixTrackSx}>
                    {/* Header row with hours */}
                    <Box sx={weekHeaderSx}>
                        <Box sx={cornerCellSx}>{intl.formatMessage({id: 'daysHours'})}</Box>
                        {HOURS.map((hour) => (
                            <Box
                                key={hour}
                                sx={{
                                    ...hourHeaderSx,
                                    ...(!readOnly && {
                                        cursor: 'pointer',
                                        '&:hover': {backgroundColor: '#4a4a6a'},
                                    }),
                                }}
                                onClick={!readOnly ? () => handleHourClick(hour) : undefined}
                                title={!readOnly ? "Click to select all cells in this hour" : undefined}
                            >
                                {String(hour).padStart(2, '0')}
                            </Box>
                        ))}
                    </Box>

                    {/* Rows for each day */}
                    {DAYS.map((day) => (
                        <Box key={day} sx={weekRowSx}>
                            <Box
                                sx={{
                                    ...dayNameSx,
                                    ...(!readOnly && {
                                        cursor: 'pointer',
                                        '&:hover': {backgroundColor: '#EDEDF2'},
                                    }),
                                }}
                                onClick={!readOnly ? () => handleDayClick(day) : undefined}
                                title={!readOnly ? "Click to select all cells in this day" : undefined}
                            >
                                {intl.formatMessage({id: DAY_TRANSLATION_KEYS[day]})}
                            </Box>
                            {HOURS.map((hour) => {
                                const price = getPrice(day, hour);
                                const hasPrice = price !== undefined;
                                const cellKey = getCellKey(day, hour);
                                const isSelected = selectedCells.has(cellKey);

                                return (
                                    <Box
                                        key={`${day}-${hour}`}
                                        sx={{
                                            ...timeCellSx,
                                            backgroundColor: isSelected ? `${selectionColor} !important` : undefined,
                                            ...(!readOnly && {
                                                '&:hover': {
                                                    backgroundColor: selectionColor,
                                                    transform: 'scale(1.05)',
                                                    zIndex: 10,
                                                    boxShadow: `0 2px 12px ${borderColor}33`,
                                                    borderRadius: '4px',
                                                },
                                                '&.no-price:hover::after': {
                                                    content: '"+"',
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    fontSize: '18px',
                                                    color: borderColor,
                                                    fontWeight: 600,
                                                },
                                            }),
                                        }}
                                        className={hasPrice ? 'has-price' : 'no-price'}
                                        data-day={day}
                                        data-hour={hour}
                                        onMouseDown={(e) => handleMouseDown(day, hour, e)}
                                        onMouseEnter={() => handleMouseEnter(day, hour)}
                                    >
                                        {hasPrice && (
                                            <Box sx={{
                                                position: 'absolute',
                                                top: '50%',
                                                left: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                backgroundColor: selectionColor,
                                                borderRadius: '2px',
                                                width: '40px',
                                                height: '40px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                            }}>
                                                <Typography sx={{fontSize: '10px', fontWeight: 500, color: '#32324D'}}>
                                                    {formatPrice(price)}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
                    </Box>
                </Box>

                {/* Action buttons below matrix - Only show if not readOnly */}
                {!readOnly && (
                    <Box sx={{display: 'flex', gap: 2, marginTop: 3, alignItems: 'center'}}>
                        <Typography variant="body2" sx={{color: '#666', fontWeight: 500}}>
                            {intl.formatMessage(
                                {id: selectedCells.size === 1 ? 'cellSelected' : 'cellsSelected'},
                                {count: selectedCells.size}
                            )}
                        </Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleOpenPriceModal}
                            disabled={selectedCells.size === 0}
                            sx={{textTransform: 'none'}}
                        >
                            <FormattedMessage id="setPrice"/>
                        </Button>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={handleClearPrice}
                            disabled={selectedCells.size === 0}
                            sx={{textTransform: 'none'}}
                        >
                            <FormattedMessage id="clearPrice"/>
                        </Button>
                        {onCopyFromMonth && (
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleOpenCopyModal}
                                sx={{textTransform: 'none'}}
                            >
                                <FormattedMessage id="copyPrice"/>
                            </Button>
                        )}
                        {selectedCells.size > 0 && (
                            <Button
                                variant="text"
                                size="small"
                                onClick={handleClearSelection}
                                sx={{textTransform: 'none', color: '#666'}}
                            >
                                <FormattedMessage id="clearSelection"/>
                            </Button>
                        )}
                    </Box>
                )}
            </Box>

            {/* Set Price Modal */}
            <AMPModal
                handleClose={handleClosePriceModal}
                open={showPriceModal}
                title={intl.formatMessage({id: 'setPriceForSelectedCells'})}
                type="sm"
                fullWidth
            >
                <Box sx={{padding: 3}}>
                    <Typography variant="body2" sx={{marginBottom: 2, color: '#666'}}>
                        {intl.formatMessage({id: 'setPriceDescription'}, {count: selectedCells.size})}
                    </Typography>

                    <AMPInput
                        value={newPrice}
                        type="text"
                        label="price"
                        input="price"
                        onChange={(e) => {
                            let value = e.target.value;

                            // Allow empty
                            if (value === '') {
                                setNewPrice('');
                                return;
                            }

                            // If the user types 0 followed by a digit (not a dot), auto-add dot
                            if (/^0\d/.test(value)) {
                                value = '0.' + value.substring(1);
                            }

                            // Validate format: digits, optional dot, max 4 digits after dot
                            // Don't allow leading zeros before non-zero digits (except 0.xxxx)
                            const regex = /^\d*\.?\d{0,4}$/;
                            if (regex.test(value)) {
                                setNewPrice(value);
                            }
                        }}
                        hasError={false}
                        helperText={currency ? `${currency}/kWh` : ''}
                    />

                    <Box sx={{display: 'flex', gap: 2, justifyContent: 'flex-end', marginTop: 2}}>
                        <Button
                            variant="outlined"
                            onClick={handleClosePriceModal}
                        >
                            <FormattedMessage id="cancel"/>
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSetPrice}
                            disabled={newPrice === '' || parseFloat(newPrice) < 0 || isNaN(parseFloat(newPrice))}
                        >
                            <FormattedMessage id="setPrice"/>
                        </Button>
                    </Box>
                </Box>
            </AMPModal>

            {/* Copy Price Modal */}
            <AMPModal
                handleClose={handleCloseCopyModal}
                open={showCopyModal}
                title={intl.formatMessage({id: 'copyPriceFromMonth'})}
                type="md"
                fullWidth
            >
                <Box sx={{padding: 3}}>
                    <Typography variant="body2" sx={{marginBottom: 3, color: '#666'}}>
                        <FormattedMessage id="selectMonthToCopyFrom"/>
                    </Typography>

                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 2,
                    }}>
                        {MONTHS.map((month) => {
                            const isSelected = selectedSourceMonth === month;
                            const isCurrentMonth = currentMonth === month;
                            const seasonColor = getMonthSeasonColor(month);

                            return (
                                <Box
                                    key={month}
                                    onClick={() => !isCurrentMonth && handleSelectSourceMonth(month)}
                                    sx={{
                                        padding: '16px 12px',
                                        borderRadius: '8px',
                                        border: '2px solid',
                                        borderColor: isSelected ? seasonColor.main : '#E0E0E0',
                                        backgroundColor: isSelected ? seasonColor.light : isCurrentMonth ? '#F5F5F5' : '#fff',
                                        cursor: isCurrentMonth ? 'not-allowed' : 'pointer',
                                        opacity: isCurrentMonth ? 0.5 : 1,
                                        textAlign: 'center',
                                        transition: 'all 0.2s ease',
                                        '&:hover': !isCurrentMonth ? {
                                            borderColor: seasonColor.main,
                                            backgroundColor: seasonColor.light,
                                            transform: 'translateY(-2px)',
                                        } : {},
                                    }}
                                >
                                    <Typography sx={{
                                        fontSize: '0.875rem',
                                        fontWeight: isSelected ? 600 : 500,
                                        color: isCurrentMonth ? '#999' : '#32324D',
                                    }}>
                                        {intl.formatMessage({id: month})}
                                    </Typography>
                                </Box>
                            );
                        })}
                    </Box>

                    <Box sx={{display: 'flex', gap: 2, justifyContent: 'flex-end', marginTop: 3}}>
                        <Button
                            variant="outlined"
                            onClick={handleCloseCopyModal}
                        >
                            <FormattedMessage id="cancel"/>
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleConfirmCopy}
                            disabled={!selectedSourceMonth}
                        >
                            <FormattedMessage id="copyPrice"/>
                        </Button>
                    </Box>
                </Box>
            </AMPModal>
        </>
    );
};